import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { IoMdSend } from "react-icons/io";
import { IoChevronDown, IoClose } from "react-icons/io5";
import InfiniteScroll from "react-infinite-scroll-component";
import { twMerge } from "tailwind-merge";
import * as Yup from "yup";
import { getComplaintRepliess } from "../../apis/help-desks/get-complaint-replies";
import { readHelpdeskChat } from "../../apis/help-desks/read-helpdesk-chat";
import { sendComplaintReply } from "../../apis/help-desks/set-complaint-reply";
import socket from "../../lib/socket-io";
import { formatMessageDate } from '../../utils';
import Input from "../Input";
import { ComplaintType, reportingMap } from "./ViewDetails";

interface ChatModalProps {
    isVisible: boolean;
    onHide: () => void;
    complaint: ComplaintType | null;
    onMessageRead?: (complaintId: string) => void;
}

type MessageType = {
    complaint: string;
    from: string;
    message: string;
    status: string;
    userType: string;
    _id: string;
    createdAt: string;
};

const MessageBubble = ({
    message,
    date,
    from,
    userName,
}: {
    message: string;
    from: string;
    date: string;
    userName?: string;
}) => {
    const isAdmin = from === "admin";
    return (
        <div className={twMerge("flex flex-col w-full gap-1", isAdmin ? "self-end items-end" : "self-start items-start")}>
            {userName && (
                <p className="text-xs text-gray-500 px-1">{userName}</p>
            )}
            <div
                className={twMerge(
                    "px-3 py-2 rounded-2xl text-sm leading-relaxed break-words whitespace-normal shadow-sm",
                    isAdmin
                        ? "bg-primary text-white  w-[95%] rounded-tr-sm"
                        : "bg-gray-100 text-gray-800  w-[95%] rounded-tl-sm"
                )}
            >
                {message}
                <div className='flex justify-end'>
                    {date && (
                        <span className={twMerge("text-gray-400 text-xs px-1", isAdmin ? "text-white" : "")}>
                            {formatMessageDate(date)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const ChatModalDesktop = ({
    isVisible,
    onHide,
    complaint,
    onMessageRead,
}: ChatModalProps) => {
    const [replies, setReplies] = useState<MessageType[]>([]);
    const [isRepliesLoading, setIsRepliesLoading] = useState(false);
    const [repliesCurrentPage, setRepliesCurrentPage] = useState(0);
    const [repliesTotalDocs, setRepliesTotalDocs] = useState(1);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const currentComplaintId = useRef<string | null>(null);

    const userName = complaint?.[reportingMap[complaint?.userType!]]?.fullName || "N/A";

    const messageForm = useFormik({
        initialValues: { message: "" },
        validationSchema: Yup.object().shape({
            message: Yup.string().trim().required("This field is required"),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!complaint) return;
            const res = await sendComplaintReply({
                complaint: complaint?._id,
                to: complaint[reportingMap[complaint?.userType]]?._id,
                toUserType: complaint?.userType,
                message: values.message,
            });
            if (res?.success) {
                setReplies((prev) => [res?.data?.data, ...prev]);
                resetForm();
            }
        },
    });

    const getComplaintReplies = async () => {
        if (repliesCurrentPage < 1) setIsRepliesLoading(true);
        const res = await getComplaintRepliess(complaint?._id!, {
            sort: "-1",
            page: repliesCurrentPage + 1,
        });
        if (res.success) {
            setReplies((prev) => [...prev, ...res.data?.docs]);
            setRepliesTotalDocs(res.data?.totalDocs);
            setRepliesCurrentPage(res.data?.page);
        }
        setIsRepliesLoading(false);
    };

    const handleMarkAsRead = async () => {
        if (!complaint) return;
        const res = await readHelpdeskChat(complaint?._id);
        if (res?.success && onMessageRead) {
            onMessageRead(complaint?._id);
        }
    };

    useEffect(() => {
        const handleNewMessage = (data: { type: string; data: MessageType }) => {
            if (data.data?.complaint === currentComplaintId.current) {
                handleMarkAsRead();
                setReplies((prev) => [data.data, ...prev]);
            }
        };
        socket.on("receive_helpdesk_message_success", handleNewMessage);
        return () => {
            socket.off("receive_helpdesk_message_success", handleNewMessage);
        };
    }, [handleMarkAsRead]);

    useEffect(() => {
        currentComplaintId.current = complaint?._id || null;
    }, [complaint?._id]);

    useEffect(() => {
        if (complaint?._id && isVisible) handleMarkAsRead();
    }, [complaint?._id, isVisible]);

    useEffect(() => {
        if (complaint?._id) {
            setReplies([]);
            setRepliesCurrentPage(0);
            setRepliesTotalDocs(1);
            getComplaintReplies();
        }
    }, [complaint?._id]);

    // Reset collapsed state when opened
    useEffect(() => {
        if (isVisible) setIsCollapsed(false);
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <>
            <div
                className={twMerge(
                    "fixed bottom-0 right-6 z-50 w-80 flex flex-col rounded-t-xl shadow-2xl border border-gray-200 bg-white",
                    "transition-all duration-300 ease-in-out",
                    isCollapsed ? "h-[52px]" : "h-[420px]"
                )}
                style={{ boxShadow: "0 -2px 20px rgba(0,0,0,0.15)" }}
            >

                <div
                    className="flex items-center justify-between px-3 py-3 bg-white rounded-t-xl border-b border-gray-100 cursor-pointer select-none"
                    onClick={() => setIsCollapsed((prev) => !prev)}
                >
                    <div className="flex items-center gap-2">

                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                            {userName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-gray-900 leading-tight truncate max-w-[160px]">
                                {userName}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsCollapsed((prev) => !prev);
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <IoChevronDown
                                className={twMerge(
                                    "w-4 h-4 text-gray-500 transition-transform duration-200",
                                    isCollapsed ? "rotate-180" : ""
                                )}
                            />
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setReplies([]);
                                setRepliesCurrentPage(0);
                                messageForm.resetForm();
                                setRepliesTotalDocs(0);
                                onHide();
                            }}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <IoClose className="w-4 h-4 text-gray-500" />
                        </button>
                    </div>
                </div>


                {!isCollapsed && (
                    <>
                        <div className="flex-1 overflow-hidden bg-white">
                            {isRepliesLoading ? (
                                <div className="h-full flex items-center justify-center">
                                    <div className="loader" />
                                </div>
                            ) : replies.length === 0 ? (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-sm text-gray-400">No messages yet</p>
                                </div>
                            ) : (
                                <div
                                    id="scrollableDiv"
                                    style={{
                                        height: "100%",
                                        overflow: "auto",
                                        display: "flex",
                                        flexDirection: "column-reverse",
                                    }}
                                >
                                    <InfiniteScroll
                                        loader={
                                            <p className="text-center text-xs text-gray-400 py-2">
                                                Loading...
                                            </p>
                                        }
                                        next={getComplaintReplies}
                                        dataLength={repliesCurrentPage * 10}
                                        hasMore={repliesTotalDocs / 10 > repliesCurrentPage}
                                        inverse={true}
                                        height={290}
                                        className="flex px-3 py-2 gap-y-2 flex-col-reverse overflow-auto"
                                    >
                                        {replies.map((reply) => (
                                            <MessageBubble
                                                key={reply._id}
                                                date={reply.createdAt}
                                                from={reply.userType}
                                                message={reply.message}
                                            />
                                        ))}
                                    </InfiniteScroll>
                                </div>
                            )}
                        </div>

                        <div className="border-t border-gray-100 px-3 py-2 bg-white rounded-b-none">
                            <form
                                onSubmit={messageForm.handleSubmit}
                                className="flex items-center gap-2"
                            >
                                <Input
                                    placeholder="Write a message..."
                                    containerClassName="flex-1"
                                    className="!rounded-full !bg-gray-50 border-gray-200 text-sm py-2 px-4 min-h-0 h-9"
                                    name="message"
                                    onChange={messageForm.handleChange}
                                    disabled={messageForm.isSubmitting}
                                    value={messageForm.values.message}
                                />
                                <button
                                    type="submit"
                                    disabled={messageForm.isSubmitting || !messageForm.values.message.trim()}
                                    className="w-9 h-9 rounded-full bg-primary flex items-center justify-center flex-shrink-0 disabled:opacity-40 hover:opacity-90 transition-opacity"
                                >
                                    <IoMdSend className="text-white w-4 h-4" />
                                </button>
                            </form>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default ChatModalDesktop;